<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import QRCode from 'qrcode';
import EmptyState from '../../../../components/widgets/EmptyState.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import DuplicateInboxBanner from './channels/instagram/DuplicateInboxBanner.vue';
import EmailInboxFinish from './channels/emailChannels/EmailInboxFinish.vue';
import { useInbox } from 'dashboard/composables/useInbox';
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

const { t } = useI18n();
const route = useRoute();
const store = useStore();

const qrCodes = reactive({
  whatsapp: '',
  messenger: '',
  telegram: '',
});
const whatsappWebQrCodeUrl = ref('');
const whatsappWebPairingCode = ref('');
const isGeneratingWhatsappWebQrCode = ref(false);
const whatsappWebConnectionState = ref(null);
const isCheckingWhatsappWebConnection = ref(false);
const whatsappWebConnectionPollingInterval = ref(null);
const hasInitializedWhatsappWeb = ref(false);

const currentInbox = computed(() =>
  store.getters['inboxes/getInbox'](route.params.inbox_id)
);

// Use useInbox composable with the inbox ID
const {
  isAWhatsAppCloudChannel,
  isATwilioChannel,
  isASmsInbox,
  isALineChannel,
  isAnEmailChannel,
  isAWhatsAppChannel,
  isAFacebookInbox,
  isATelegramChannel,
  isATwilioWhatsAppChannel,
} = useInbox(route.params.inbox_id);

const hasDuplicateInstagramInbox = computed(() => {
  const instagramId = currentInbox.value.instagram_id;
  const facebookInbox =
    store.getters['inboxes/getFacebookInboxByInstagramId'](instagramId);

  return (
    currentInbox.value.channel_type === INBOX_TYPES.INSTAGRAM && facebookInbox
  );
});

const shouldShowWhatsAppWebhookDetails = computed(() => {
  return (
    isAWhatsAppCloudChannel.value &&
    currentInbox.value.provider_config?.source !== 'embedded_signup'
  );
});

const isWhatsAppEmbeddedSignup = computed(() => {
  return (
    isAWhatsAppCloudChannel.value &&
    currentInbox.value.provider_config?.source === 'embedded_signup'
  );
});

const isAWhatsAppWebChannel = computed(() => {
  return currentInbox.value?.channel_type === INBOX_TYPES.WHATSAPP_WEB;
});

const whatsappWebConnectionStatus = computed(() => {
  if (!whatsappWebConnectionState.value) return null;
  const state = String(whatsappWebConnectionState.value).toLowerCase();
  if (state === 'open' || state === 'connected') return 'connected';
  if (state === 'connecting') return 'connecting';
  return 'disconnected';
});

const whatsappWebStatusColor = computed(() => {
  if (whatsappWebConnectionStatus.value === 'connected') return 'bg-green-500';
  if (whatsappWebConnectionStatus.value === 'connecting') return 'bg-yellow-500';
  return 'bg-red-500';
});

const whatsappWebStatusText = computed(() => {
  if (whatsappWebConnectionStatus.value === 'connected') {
    return t('INBOX_MGMT.QRCODE_CONNECTOR.CONNECTED');
  }
  if (whatsappWebConnectionStatus.value === 'connecting') {
    return t('INBOX_MGMT.QRCODE_CONNECTOR.CONNECTING');
  }
  return t('INBOX_MGMT.QRCODE_CONNECTOR.DISCONNECTED');
});

const message = computed(() => {
  if (isATwilioChannel.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.ADD.TWILIO.API_CALLBACK.SUBTITLE'
    )}`;
  }

  if (isASmsInbox.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.ADD.SMS.BANDWIDTH.API_CALLBACK.SUBTITLE'
    )}`;
  }

  if (isALineChannel.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.ADD.LINE_CHANNEL.API_CALLBACK.SUBTITLE'
    )}`;
  }

  if (isAWhatsAppCloudChannel.value && shouldShowWhatsAppWebhookDetails.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.ADD.WHATSAPP.API_CALLBACK.SUBTITLE'
    )}`;
  }

  if (currentInbox.value.web_widget_script) {
    return t('INBOX_MGMT.FINISH.WEBSITE_SUCCESS');
  }

  if (isWhatsAppEmbeddedSignup.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.FINISH.WHATSAPP_QR_INSTRUCTION'
    )}`;
  }

  return t('INBOX_MGMT.FINISH.MESSAGE');
});

async function generateQRCode(platform, identifier) {
  if (!identifier || !identifier.trim()) {
    // eslint-disable-next-line no-console
    console.warn(`Invalid identifier for ${platform} QR code`);
    return;
  }

  try {
    const platformUrls = {
      whatsapp: id => `https://wa.me/${id}`,
      messenger: id => `https://m.me/${id}`,
      telegram: id => `https://t.me/${id}`,
    };

    const url = platformUrls[platform](identifier);
    const qrDataUrl = await QRCode.toDataURL(url);
    qrCodes[platform] = qrDataUrl;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Error generating ${platform} QR code:`, error);
    qrCodes[platform] = '';
  }
}

async function generateQRCodes() {
  if (!currentInbox.value) return;

  // WhatsApp (both Cloud and Twilio)
  if (currentInbox.value.phone_number && isAWhatsAppChannel.value) {
    // For Twilio WhatsApp, phone_number format is "whatsapp:+1234567890"
    // Extract just the phone number part for QR code generation
    const phoneNumber = currentInbox.value.phone_number.replace(
      'whatsapp:',
      ''
    );
    await generateQRCode('whatsapp', phoneNumber);
  }

  // Facebook Messenger
  if (currentInbox.value.page_id && isAFacebookInbox.value) {
    await generateQRCode('messenger', currentInbox.value.page_id);
  }

  // Telegram
  if (isATelegramChannel.value && currentInbox.value.bot_name) {
    await generateQRCode('telegram', currentInbox.value.bot_name);
  }
}

async function generateWhatsappWebQRCode() {
  if (!isAWhatsAppWebChannel.value || !currentInbox.value?.id) return;

  isGeneratingWhatsappWebQrCode.value = true;
  try {
    const response = await InboxesAPI.generateQrcode(currentInbox.value.id);
    const { code, pairingCode, connected, state } = response.data;
    if (connected || state === 'open') {
      whatsappWebConnectionState.value = 'open';
      stopWhatsappWebConnectionPolling();
      return;
    }
    if (!code) return;
    whatsappWebPairingCode.value = pairingCode || '';

    if (code.startsWith('data:image/')) {
      whatsappWebQrCodeUrl.value = code;
      return;
    }

    whatsappWebQrCodeUrl.value = await QRCode.toDataURL(code, {
      width: 256,
      margin: 2,
      color: {
        dark: '#000000',
        light: '#FFFFFF',
      },
    });
  } catch (error) {
    useAlert(error.response?.data?.error || t('INBOX_MGMT.QRCODE_CONNECTOR.GENERATE_QRCODE_ERROR'));
  } finally {
    isGeneratingWhatsappWebQrCode.value = false;
  }
}

async function checkWhatsappWebConnectionState() {
  if (!isAWhatsAppWebChannel.value || !currentInbox.value?.id) return;

  isCheckingWhatsappWebConnection.value = true;
  try {
    const response = await InboxesAPI.getConnectionState(currentInbox.value.id);
    const state =
      response.data?.state ||
      response.data?.instance?.state ||
      response.data?.connectionState;
    whatsappWebConnectionState.value = state;
    if (whatsappWebConnectionStatus.value === 'connected') {
      stopWhatsappWebConnectionPolling();
    }
  } catch (error) {
    whatsappWebConnectionState.value = 'close';
  } finally {
    isCheckingWhatsappWebConnection.value = false;
  }
}

function startWhatsappWebConnectionPolling() {
  stopWhatsappWebConnectionPolling();
  checkWhatsappWebConnectionState();
  whatsappWebConnectionPollingInterval.value = setInterval(() => {
    checkWhatsappWebConnectionState();
  }, 10000);
}

function stopWhatsappWebConnectionPolling() {
  if (whatsappWebConnectionPollingInterval.value) {
    clearInterval(whatsappWebConnectionPollingInterval.value);
    whatsappWebConnectionPollingInterval.value = null;
  }
}

// Watch for currentInbox changes and regenerate QR codes when available
watch(
  currentInbox,
  newInbox => {
    if (!newInbox) return;

    generateQRCodes();
    if (newInbox.channel_type === INBOX_TYPES.WHATSAPP_WEB) {
      if (!hasInitializedWhatsappWeb.value) {
        hasInitializedWhatsappWeb.value = true;
        generateWhatsappWebQRCode();
        startWhatsappWebConnectionPolling();
      }
      // Ignore subsequent inbox Vuex updates — don't regenerate QR or restart polling
    } else {
      stopWhatsappWebConnectionPolling();
    }
  },
  { immediate: true }
);

onMounted(() => {
  generateQRCodes();
  // WhatsApp Web init handled by watch({ immediate: true }) above
});

onBeforeUnmount(() => {
  stopWhatsappWebConnectionPolling();
});
</script>

<template>
  <div class="overflow-auto col-span-6 p-6 w-full h-full">
    <DuplicateInboxBanner
      v-if="hasDuplicateInstagramInbox"
      :content="$t('INBOX_MGMT.ADD.INSTAGRAM.NEW_INBOX_SUGGESTION')"
    />
    <EmptyState
      :title="$t('INBOX_MGMT.FINISH.TITLE')"
      :message="isAnEmailChannel && !currentInbox.provider ? '' : message"
      :button-text="$t('INBOX_MGMT.FINISH.BUTTON_TEXT')"
    >
      <div class="w-full text-center">
        <div class="my-4 mx-auto max-w-[70%]">
          <woot-code
            v-if="currentInbox.web_widget_script"
            :script="currentInbox.web_widget_script"
          />
        </div>
        <div class="w-[50%] max-w-[50%] ml-[25%]">
          <woot-code
            v-if="isATwilioWhatsAppChannel"
            lang="html"
            :script="currentInbox.callback_webhook_url"
          />
        </div>
        <div
          v-if="shouldShowWhatsAppWebhookDetails"
          class="w-[50%] max-w-[50%] ml-[25%]"
        >
          <p class="mt-8 font-medium text-n-slate-11">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.API_CALLBACK.WEBHOOK_URL') }}
          </p>
          <woot-code lang="html" :script="currentInbox.callback_webhook_url" />
          <p class="mt-8 font-medium text-n-slate-11">
            {{
              $t(
                'INBOX_MGMT.ADD.WHATSAPP.API_CALLBACK.WEBHOOK_VERIFICATION_TOKEN'
              )
            }}
          </p>
          <woot-code
            lang="html"
            :script="currentInbox.provider_config.webhook_verify_token"
          />
        </div>
        <div class="w-[50%] max-w-[50%] ml-[25%]">
          <woot-code
            v-if="isALineChannel"
            lang="html"
            :script="currentInbox.callback_webhook_url"
          />
        </div>
        <div class="w-[50%] max-w-[50%] ml-[25%]">
          <woot-code
            v-if="isASmsInbox"
            lang="html"
            :script="currentInbox.callback_webhook_url"
          />
        </div>
        <EmailInboxFinish
          v-if="isAnEmailChannel && !currentInbox.provider"
          :inbox="currentInbox"
          :inbox-id="$route.params.inbox_id"
        />
        <div
          v-if="isAWhatsAppWebChannel"
          class="flex flex-col gap-3 items-center mt-8"
        >
          <div
            v-if="
              whatsappWebConnectionState !== null &&
              whatsappWebConnectionStatus !== 'connected'
            "
            class="flex items-center gap-2"
          >
            <div
              class="w-3 h-3 rounded-full"
              :class="whatsappWebStatusColor"
            />
            <span class="text-sm text-n-slate-11">
              {{ whatsappWebStatusText }}
              <span
                v-if="isCheckingWhatsappWebConnection"
                class="text-xs text-n-slate-10 ml-1"
              >
                ...
              </span>
            </span>
          </div>
          <p
            v-if="whatsappWebConnectionStatus !== 'connected'"
            class="mt-2 text-sm text-n-slate-9"
          >
            {{ $t('INBOX_MGMT.QRCODE_CONNECTOR.SCAN_INSTRUCTION') }}
          </p>
          <div
            v-if="whatsappWebConnectionStatus === 'connected'"
            class="flex flex-col items-center gap-2 py-4"
          >
            <div
              class="w-24 h-24 rounded-full bg-green-500 flex items-center justify-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="3"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="w-12 h-12 text-white"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </div>
            <p class="text-sm font-medium text-green-600">
              {{ $t('INBOX_MGMT.QRCODE_CONNECTOR.CONNECTED') }}
            </p>
          </div>
          <div
            v-else-if="whatsappWebQrCodeUrl"
            class="rounded-lg shadow outline-1 outline-n-strong outline bg-white p-2"
          >
            <img
              :src="whatsappWebQrCodeUrl"
              alt="WhatsApp Web QR Code"
              class="rounded-lg size-56"
            />
          </div>
          <div
            v-if="
              whatsappWebConnectionStatus !== 'connected' &&
              whatsappWebPairingCode
            "
            class="w-full max-w-sm text-center"
          >
            <p class="text-sm text-n-slate-11 mb-1">
              {{ $t('INBOX_MGMT.QRCODE_CONNECTOR.PAIRING_CODE_LABEL') }}
            </p>
            <code class="inline-block px-3 py-2 rounded-md bg-n-alpha-2 text-sm">
              {{ whatsappWebPairingCode }}
            </code>
          </div>
          <NextButton
            :is-loading="isGeneratingWhatsappWebQrCode"
            :label="
              whatsappWebQrCodeUrl
                ? $t('INBOX_MGMT.QRCODE_CONNECTOR.REGENERATE_BUTTON')
                : $t('INBOX_MGMT.QRCODE_CONNECTOR.GENERATE_BUTTON')
            "
            @click="generateWhatsappWebQRCode"
          />
        </div>
        <div
          v-if="isAWhatsAppChannel && qrCodes.whatsapp"
          class="flex flex-col gap-3 items-center mt-8"
        >
          <p class="mt-2 text-sm text-n-slate-9">
            {{ $t('INBOX_MGMT.FINISH.WHATSAPP_QR_INSTRUCTION') }}
          </p>
          <div class="rounded-lg shadow outline-1 outline-n-strong outline">
            <img
              :src="qrCodes.whatsapp"
              alt="WhatsApp QR Code"
              class="rounded-lg size-48"
            />
          </div>
        </div>
        <div
          v-if="isAFacebookInbox && qrCodes.messenger"
          class="flex flex-col gap-3 items-center mt-8"
        >
          <p class="mt-2 text-sm text-n-slate-9">
            {{ $t('INBOX_MGMT.FINISH.MESSENGER_QR_INSTRUCTION') }}
          </p>
          <div class="rounded-lg shadow outline-1 outline-n-strong outline">
            <img
              :src="qrCodes.messenger"
              alt="Messenger QR Code"
              class="rounded-lg size-48"
            />
          </div>
        </div>
        <div
          v-if="isATelegramChannel && qrCodes.telegram"
          class="flex flex-col gap-4 items-center mt-8"
        >
          <p class="mt-2 text-sm text-n-slate-9">
            {{ $t('INBOX_MGMT.FINISH.TELEGRAM_QR_INSTRUCTION') }}
          </p>

          <div class="rounded-lg shadow outline-1 outline-n-strong outline">
            <img
              :src="qrCodes.telegram"
              alt="Telegram QR Code"
              class="rounded-lg size-48"
            />
          </div>
        </div>
        <div class="flex gap-2 justify-center mt-4">
          <router-link
            :to="{
              name: 'settings_inbox_show',
              params: { inboxId: $route.params.inbox_id },
            }"
          >
            <NextButton
              outline
              slate
              :label="$t('INBOX_MGMT.FINISH.MORE_SETTINGS')"
            />
          </router-link>
          <router-link
            :to="{
              name: 'inbox_dashboard',
              params: { inboxId: $route.params.inbox_id },
            }"
          >
            <NextButton
              solid
              teal
              :label="$t('INBOX_MGMT.FINISH.BUTTON_TEXT')"
            />
          </router-link>
        </div>
      </div>
    </EmptyState>
  </div>
</template>
